import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Map, 
  Package, 
  Wrench, 
  Clock, 
  Users, 
  FileCheck, 
  Settings,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Truck,
  ClipboardList,
  ListChecks,
  CalendarDays,
  Moon,
  Sun,
  Monitor,
  Shield,
  Building2,
  ChevronDown,
  Briefcase
} from 'lucide-react';
import { useAuth, isManager } from '../context/AuthContext';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useTheme } from '../hooks/useTheme';
import { Logo } from './Logo';

// Tenant logo – renders custom uploaded logo if present, or falls back to our official vector Logo
const TenantLogo: React.FC<{ height?: number }> = ({ height = 32 }) => {
  const { currentTenant } = useAuth();
  const isCustomLogo = currentTenant?.logoUrl && 
    (currentTenant.logoUrl.startsWith('http://') || currentTenant.logoUrl.startsWith('https://') || currentTenant.logoUrl.startsWith('data:')) &&
    !['/logo.png', '/logo-mobile.png', '/swi-logo.png', '/swi-logo-real.png'].includes(currentTenant.logoUrl);

  if (isCustomLogo && currentTenant?.logoUrl) {
    return (
      <img 
        src={currentTenant.logoUrl} 
        alt={currentTenant.name || "Betriebszentrale"} 
        height={height} 
        style={{ display: 'block', objectFit: 'contain', maxWidth: '160px' }} 
      />
    );
  }
  return (
    <div style={{ color: 'var(--ms-text-primary)' }}>
      <Logo size={30} showText={true} color="var(--ms-text-primary)" />
    </div>
  );
};

export const Sidebar: React.FC<{ isMobileOpen?: boolean; closeMobile?: () => void }> = ({ isMobileOpen, closeMobile }) => {
  const [expanded, setExpanded] = useState(() => typeof window !== 'undefined' ? window.innerWidth > 1024 : true);
  const { currentUser, currentTenant, logout } = useAuth();
  const { isOnline, pendingCount, isSyncing } = useOfflineSync();
  const { theme, cycleTheme } = useTheme();

  // Auto-adapt for Tablet Portrait vs Landscape
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024 && window.innerWidth > 768) {
        setExpanded(false); // Rail mode on tablets
      } else if (window.innerWidth > 1024) {
        setExpanded(true); // Full expanded on desktop/laptop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    'Auftragsmanagement': true,
    'Kunden & Verkauf': false,
    'Ressourcen': false,
    'Personal': false,
    'System & Admin': false
  });
  const [activeFlyout, setActiveFlyout] = useState<string | null>(null);

  const themeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const themeLabel = theme === 'dark' ? 'Dunkel' : theme === 'light' ? 'Hell' : 'System';
  const ThemeIcon = themeIcon;

  const dashboardItem = { path: '/', label: 'Dashboard', icon: LayoutDashboard, moduleId: null };

  const menuGroups = [
    {
      name: 'Kunden & Verkauf',
      icon: Briefcase,
      items: [
        { path: '/crm', label: 'Kunden (CRM)', icon: Users, moduleId: 'crm' },
        { path: '/angebote', label: 'Angebote', icon: FileText, moduleId: 'angebote' },
        { path: '/rechnungen', label: 'Rechnungen', icon: FileCheck, moduleId: 'rechnungen' }
      ]
    },
    {
      name: 'Auftragsmanagement',
      icon: ClipboardList,
      items: [
        { path: '/disposition', label: 'Disposition', icon: ListChecks, moduleId: 'auftraege' },
        { path: '/auftraege', label: 'Aufträge', icon: ClipboardList, moduleId: 'auftraege' },
        { path: '/einsatzplan', label: 'Einsatzplan', icon: CalendarDays, moduleId: 'einsatzplan' },
        { path: '/karte', label: 'Karte', icon: Map, moduleId: 'karte' }
      ]
    },
    {
      name: 'Ressourcen',
      icon: Package,
      items: [
        { path: '/lager', label: 'Lager', icon: Package, moduleId: 'lager' },
        { path: '/maschinen', label: 'Maschinen', icon: Wrench, moduleId: 'maschinen' },
        { path: '/fuhrpark', label: 'Fuhrpark', icon: Truck, moduleId: 'fuhrpark' },
        { path: '/lieferanten', label: 'Lieferanten', icon: Building2, moduleId: 'lieferanten' }
      ]
    },
    {
      name: 'Personal',
      icon: Clock,
      items: [
        { path: '/zeiterfassung', label: 'Zeiterfassung', icon: Clock, moduleId: 'zeiterfassung' },
        { path: '/mitarbeiter', label: 'Mitarbeiter', icon: Users, moduleId: 'mitarbeiter' }
      ]
    },
    {
      name: 'System & Admin',
      icon: Settings,
      items: [
        { path: '/admin', label: 'Verwaltung', icon: Settings, moduleId: 'verwaltung' },
        { path: '/protokoll', label: 'Protokoll', icon: Shield, moduleId: 'protokoll' }
      ]
    }
  ];

  const hasAccessToModule = (moduleId: string | null) => {
    if (!currentUser) return false;
    if (moduleId === null) return true;
    if (currentUser.role === 'Admin' && moduleId === 'verwaltung') return true;
    return (currentUser.activeModules || []).includes(moduleId);
  };

  const toggleGroup = (groupName: string) => {
    setOpenGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    if (!expanded) setExpanded(true);
  };

  const NavItemRender = ({ item, isSubItem = false }: { item: { path: string; label: string; icon: any }; isSubItem?: boolean }) => (
    <NavLink 
      key={item.path} 
      to={item.path} 
      onClick={closeMobile}
      title={!expanded && !isSubItem ? item.label : undefined}
      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: isSubItem ? '8px 12px 8px 30px' : '9px 12px',
        borderRadius: 'var(--sys-shape-corner-small)',
        textDecoration: 'none',
        color: isActive ? 'var(--ms-blue-primary)' : 'var(--ms-text-primary)',
        fontWeight: isActive ? 600 : 400,
        backgroundColor: isActive ? 'var(--ms-blue-light)' : 'transparent',
        borderLeft: isActive ? '3px solid var(--ms-blue-primary)' : '3px solid transparent',
        transition: 'all var(--transition-fast)',
        fontSize: '0.865rem',
        justifyContent: expanded ? 'flex-start' : 'center',
        position: 'relative'
      })}
    >
      {!isSubItem && <item.icon size={18} color="currentColor" />}
      {isSubItem && expanded && <item.icon size={15} color="currentColor" style={{ position: 'absolute', left: '11px' }} />}
      {expanded && <span>{item.label}</span>}
    </NavLink>
  );

  return (
    <>
      <div 
        className={`sidebar-overlay ${isMobileOpen ? 'active' : ''}`} 
        onClick={closeMobile}
      />
      <aside className={`sidebar ${expanded ? 'expanded' : ''} ${isMobileOpen ? 'mobile-open' : ''}`} style={{ 
        width: expanded ? '250px' : '64px',
        transition: 'width 200ms cubic-bezier(0.2, 0, 0, 1), transform 300ms cubic-bezier(0.2, 0, 0, 1)', 
        overflowY: expanded ? 'auto' : 'visible',
        overflowX: 'visible'
      }}>
        {/* Sidebar Header with Brand & Collapse Button */}
        <div style={{ padding: '14px 12px', display: 'flex', alignItems: 'center', justifyContent: expanded ? 'space-between' : 'center', width: '100%', borderBottom: '1px solid var(--ms-border-subtle)' }}>
          {expanded ? <TenantLogo /> : <Logo size={26} showText={false} />}
          <button 
            onClick={() => setExpanded(!expanded)} 
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              color: 'var(--ms-text-secondary)', 
              display: 'flex', 
              alignItems: 'center', 
              padding: '6px',
              borderRadius: '4px',
              transition: 'background var(--transition-fast)'
            }}
            title={expanded ? "Menü einklappen" : "Menü ausklappen"}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--ms-bg-surface-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            {expanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>

        {/* Main Navigation Menu */}
        <nav style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '0 10px', gap: '3px', marginTop: '12px', flex: 1 }}>
          <NavItemRender item={dashboardItem} />

          {menuGroups.map(group => {
            const visibleGroupItems = group.items.filter(item => hasAccessToModule(item.moduleId));
            if (visibleGroupItems.length === 0) return null;

            const isOpen = openGroups[group.name];

            if (!expanded) {
              // Rail Mode: MS 365 Flyout Menu on Hover
              const isFlyoutActive = activeFlyout === group.name;
              return (
                <div 
                  key={group.name}
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setActiveFlyout(group.name)}
                  onMouseLeave={() => setActiveFlyout(null)}
                >
                  <button 
                    onClick={() => toggleGroup(group.name)}
                    style={{ 
                      display: 'flex', justifyContent: 'center', alignItems: 'center', 
                      width: '40px', height: '40px', margin: '2px auto',
                      background: isFlyoutActive ? 'var(--ms-blue-light)' : 'transparent',
                      border: 'none', cursor: 'pointer',
                      color: isFlyoutActive ? 'var(--ms-blue-primary)' : 'var(--ms-text-secondary)',
                      borderRadius: 'var(--sys-shape-corner-small)', 
                      transition: 'all var(--transition-fast)'
                    }}
                    title={group.name}
                  >
                    <group.icon size={19} />
                  </button>

                  {/* Floating MS 365 Hover Flyout */}
                  {isFlyoutActive && (
                    <div 
                      className="animate-pop-in"
                      style={{
                        position: 'absolute',
                        left: '48px',
                        top: '-4px',
                        backgroundColor: 'var(--ms-bg-surface)',
                        border: '1px solid var(--ms-border)',
                        borderRadius: 'var(--sys-shape-corner-medium)',
                        boxShadow: 'var(--sys-elevation-3)',
                        padding: '6px 4px',
                        minWidth: '210px',
                        zIndex: 10000,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}
                    >
                      <div style={{
                        padding: '6px 10px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: 'var(--ms-text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '1px solid var(--ms-border-subtle)',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <group.icon size={13} />
                        <span>{group.name}</span>
                      </div>
                      {visibleGroupItems.map(item => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={() => {
                            setActiveFlyout(null);
                            if (closeMobile) closeMobile();
                          }}
                          style={({ isActive }) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            textDecoration: 'none',
                            fontSize: '0.85rem',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? 'var(--ms-blue-primary)' : 'var(--ms-text-primary)',
                            backgroundColor: isActive ? 'var(--ms-blue-light)' : 'transparent',
                            transition: 'background var(--transition-fast)'
                          })}
                        >
                          <item.icon size={15} color="currentColor" />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Expanded Mode: Accordion Group
            return (
              <div key={group.name} style={{ display: 'flex', flexDirection: 'column', marginTop: '6px' }}>
                <button 
                  onClick={() => toggleGroup(group.name)}
                  style={{ 
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--ms-text-secondary)', fontWeight: 600, fontSize: '0.76rem',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                    borderRadius: '4px',
                    transition: 'background var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--ms-bg-surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <group.icon size={15} />
                    {group.name}
                  </div>
                  <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
                </button>
                
                {isOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' }}>
                    {visibleGroupItems.map(item => (
                      <NavItemRender key={item.path} item={item} isSubItem={true} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div style={{ 
          padding: '14px', 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: expanded ? 'flex-start' : 'center', 
          gap: '10px', 
          borderTop: '1px solid var(--ms-border-subtle)',
          backgroundColor: 'var(--ms-bg-surface)'
        }}>
           {expanded && (
             <div style={{ fontSize: '0.85rem', width: '100%' }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                 <p style={{ fontWeight: 600, color: 'var(--ms-text-primary)' }}>{currentUser?.name}</p>
                 <span className="ms-badge neutral" style={{ fontSize: '0.68rem', padding: '1px 6px' }}>
                   {currentUser?.role === 'Admin' ? 'Admin' : 'Mitarbeiter'}
                 </span>
               </div>
             </div>
           )}

           <div style={{ display: 'flex', gap: '6px', width: '100%', justifyContent: expanded ? 'flex-start' : 'center' }}>
             <button
               onClick={cycleTheme}
               title={`Theme: ${themeLabel}`}
               className="btn subtle"
               style={{
                 display: 'flex', alignItems: 'center', gap: '6px',
                 border: '1px solid var(--ms-border)',
                 borderRadius: 'var(--sys-shape-corner-full)',
                 color: 'var(--ms-text-secondary)',
                 padding: expanded ? '4px 12px' : '6px',
                 fontSize: '0.78rem', 
                 fontWeight: 500,
                 flex: expanded ? 1 : 'none'
               }}
             >
               <ThemeIcon size={16} />
               {expanded && <span>{themeLabel}</span>}
             </button>

             <button 
                onClick={logout}
                className="btn subtle"
                style={{ 
                  display: 'flex', alignItems: 'center', gap: '6px',
                  color: 'var(--ms-status-error)', 
                  padding: expanded ? '4px 10px' : '6px',
                  fontSize: '0.78rem', 
                  fontWeight: 500,
                  borderRadius: 'var(--sys-shape-corner-full)',
                  border: '1px solid transparent'
                }}
                title="Abmelden"
              >
                <LogOut size={16} />
                {expanded && <span>Abmelden</span>}
              </button>
           </div>

            {expanded && (
              <>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px', 
                  fontSize: '0.72rem', 
                  padding: '4px 8px', 
                  borderRadius: 'var(--sys-shape-corner-full)', 
                  backgroundColor: isOnline ? 'var(--ms-status-success-bg)' : 'var(--ms-status-error-bg)', 
                  color: isOnline ? 'var(--ms-status-success)' : 'var(--ms-status-error)', 
                  fontWeight: 600,
                  width: '100%'
                }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isOnline ? 'var(--ms-status-success)' : 'var(--ms-status-error)', flexShrink: 0 }} />
                  <span className="tabular-nums">
                    {isSyncing ? 'Synchronisiert...' : isOnline ? 'Online & Verbunden' : `Offline${pendingCount > 0 ? ` (${pendingCount} lokal)` : ''}`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '0.72rem', color: 'var(--ms-text-muted)' }}>
                  <NavLink to="/impressum" style={{ color: 'inherit', textDecoration: 'none' }}>Impressum</NavLink>
                  <NavLink to="/datenschutz" style={{ color: 'inherit', textDecoration: 'none' }}>Datenschutz</NavLink>
                </div>
              </>
            )}
        </div>
      </aside>
    </>
  );
};
